import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { IbanFormatPipe } from '../../../../../core/pipes/iban-format.pipe';
import { ConstitutionShareholdersStore } from '../../stores/constitution-shareholders.store';
import { ConstitutionCapitalStore } from '../../stores/constitution-capital.store';
import { Router } from '@angular/router';
import { navigateToRelatedClient } from '../../../../../core/utils/client-navigation.util';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { faTrashCan, faUser } from '@fortawesome/pro-light-svg-icons';
import { faCheck, faCircleInfo, faDiamondExclamation, faPenToSquare } from '@fortawesome/pro-solid-svg-icons';
import { formatShareAmountEs } from '../../utils/format-share-amount';
import { formatTituloLabel } from '../../utils/titulo-label.util';
import {
  buildGroupedShareSeriesForModal,
  formatEuro,
  formatPct4,
} from '../../utils/socios-display.util';

/** Fila de numeración dentro de un grupo por serie (modal Acciones Totales). */
export interface GroupedNumeracionRow {
  globalIndex: number;
  sharesNumberFrom: number;
  sharesNumberTo: number;
  sharesCount: number;
  titulo: string | undefined;
  ownershipPercentage: number | null;
  adjustedShares: number;
  vnEur: number | null;
  isUsufructo: boolean;
}

export interface GroupedShareSeries {
  /** Para @for track: distingue capital sin series vs serie null. */
  groupId: string;
  seriesId: number | null;
  seriesName: string;
  /** Total acciones emitidas de la serie en el capital (denominador en TOTAL serie). */
  seriesTotalShares: number;
  /** Σ acciones del socio en esta serie (filas del modal). */
  partnerSharesInSeries: number;
  nominalValue: number;
  rows: GroupedNumeracionRow[];
  subtotalAdjusted: number;
  subtotalVn: number;
  hasNonUsufructo: boolean;
}

/** Fila de la tabla principal de socios, una por título por socio. */
export interface SocioTituloRow {
  id: number;
  name: string;
  documentNumber: string;
  sharesCountNonUsuf: number;
  totalAdjusted: number;
  totalVn: number;
  participationPercent: string;
  isPresident: boolean;
  isSecretary: boolean;
  isWorker: boolean;
  rowCount: number;
  associatedClientId: number | null;
  socioIndex: number;
  titulo: string;
  tituloLabel: string;
  sharesCountForTitulo: number;
  sharesCountAdjustedForTitulo: number;
  isFirst: boolean;
  isUsufructo: boolean;
}

export interface SeriesParticipationTotal {
  seriesName: string;
  adjusted: number;
  denominator: number;
  fractionLabel: string;
  percentLabel: string;
}

@Component({
  selector: 'app-constitution-socios-section',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DropdownModule,
    InputTextModule,
    CheckboxModule,
    InputNumberModule,
    IbanFormatPipe,
    FontAwesomeModule,
    InputTextareaModule,
  ],
  templateUrl: './constitution-socios-section.component.html',
  styleUrl: '../../constitution-form.component.scss',
})
export class ConstitutionSociosSectionComponent {

  readonly store = inject(ConstitutionShareholdersStore);
  readonly capital = inject(ConstitutionCapitalStore);
  private readonly router = inject(Router);

  clientId = input<number | null>(null);
  activeTab = input<string>('constitution');
  deferPersistence = input(false);

  deleteSocio(index: number): void {
    const nombre = this.store.socios()[index]?.name || 'este socio';
    if (window.confirm(`¿Eliminar a ${nombre}? Se perderán sus participaciones.`)) {
      this.store.deleteSocio(index, this.deferPersistence());
    }
  }
  faTrash = faTrashCan;
  faEdit = faPenToSquare;
  faUser = faUser;
  diamondExclamation = faDiamondExclamation;
  circleInfo = faCircleInfo;
  check = faCheck;

  /** Recuentos de acciones en plantilla (agrupación, badges). */
  readonly formatShareAmount = formatShareAmountEs;
  readonly formatEuro = formatEuro;

  /** Hover unificado por socio (varias `<tr>` con rowspan). */
  readonly hoveredSocioIndex = signal<number | null>(null);
  /** Hover unificado por serie en tabla Acciones totales del modal. */
  readonly hoveredShareGroupId = signal<string | null>(null);

  /** Capital social total (€): Σ (acciones × VN) por serie, o serie única. */
  readonly totalCapitalSocialEuro = computed(() => {
    const cap = this.capital;
    if (cap.hasSeries()) {
      return cap.series().reduce(
        (acc, s) => acc + (Number(s.totalShares) || 0) * (Number(s.nominalValue) || 0),
        0
      );
    }
    const shares = cap.singleSeriesTotal();
    const nv = Number(cap.capitalSocialForm.get('nominalValue')?.value) || 0;
    return shares * nv;
  });

  readonly enrichedSociosTableConfig = computed(() => {
    const config = this.store.configSociosTable();
    const totalCapitalEuro = this.totalCapitalSocialEuro();
    if (!config) return config;

    const deleteHeader = config.headers.find((h: any) => h.key === 'delete');
    const headersWithoutDelete = config.headers.filter((h: any) => h.key !== 'delete');
    const headers = [
      ...headersWithoutDelete,
      { name: '% Participación', key: 'participationPercent', textAlign: 'right' },
      ...(deleteHeader ? [deleteHeader] : [])
    ];

    const data = config.data.map((row: any) => {
      const vnEuro = this.partnerTotalVnEuro(row.shareNumberings);
      return {
        ...row,
        participationPercent:
          totalCapitalEuro > 0
            ? formatPct4((vnEuro / totalCapitalEuro) * 100)
            : '-'
      };
    });

    return { ...config, headers, data };
  });

  /** Nº acciones totales socios: Σ acciones ajustadas por socio (shares × % titularidad), sin usufructo. */
  readonly totalSociosShares = computed(() =>
    this.store.socios().reduce((sum, s) => {
      const numberings: any[] = (s as any).shareNumberings || [];
      if (numberings.length === 0) return sum + (Number(s.sharesCount) || 0);
      return sum + this.partnerTotalAdjustedShares(numberings);
    }, 0)
  );

  /** Tabla principal de socios: una fila por título, con rowspan para las celdas compartidas. */
  readonly sociosTituloRows = computed((): SocioTituloRow[] => {
    const totalCapitalEuro = this.totalCapitalSocialEuro();
    const result: SocioTituloRow[] = [];

    this.store.socios().forEach((s, socioIndex, arr) => {
      const numberings: any[] = (s as any).shareNumberings || [];

      // Agrupar por título (clave normalizada)
      const tituloOrder: string[] = [];
      const tituloMap = new Map<string, { sharesCount: number; adjusted: number }>();

      numberings.forEach(n => {
        const key = (n.titulo ?? '').trim().toLowerCase() || '__none__';
        if (!tituloMap.has(key)) {
          tituloMap.set(key, { sharesCount: 0, adjusted: 0 });
          tituloOrder.push(key);
        }
        const entry = tituloMap.get(key)!;
        const pct = Number(n.ownershipPercentage ?? 0) / 100;
        entry.sharesCount += n.sharesCount ?? 0;
        entry.adjusted += (n.sharesCount ?? 0) * pct;
      });

      // Si no hay numeraciones, mostrar una fila vacía
      if (tituloOrder.length === 0) tituloOrder.push('__none__');
      if (!tituloMap.has('__none__')) tituloMap.set('__none__', { sharesCount: 0, adjusted: 0 });

      const sharesCountNonUsuf = numberings
        .filter(n => !this.isUsufructoTitulo(n.titulo))
        .reduce((acc, n) => acc + (n.sharesCount ?? 0), 0);

      const totalAdjusted = this.partnerTotalAdjustedShares(numberings);

      const totalVn = this.partnerTotalVnEuro(numberings);
      const participationPercent =
        totalCapitalEuro > 0 ? formatPct4((totalVn / totalCapitalEuro) * 100) : '-';

      const rowCount = tituloOrder.length;

      tituloOrder.forEach((key, i) => {
        const entry = tituloMap.get(key)!;
        const isUsufructo = this.isUsufructoTitulo(key === '__none__' ? '' : key);
        result.push({
          id: s.id,
          name: s.name,
          documentNumber: (s as any).documentNumber ?? '',
          sharesCountNonUsuf,
          totalAdjusted,
          totalVn,
          participationPercent,
          isPresident: !!(s as any).isGeneralMeetingPresident,
          isSecretary: !!(s as any).isGeneralMeetingSecretary,
          isWorker: !!(s as any).isWorkerPartner,
          rowCount,
          associatedClientId: (s as any).associatedClient?.id ?? null,
          socioIndex,
          titulo: key === '__none__' ? '' : key,
          tituloLabel: key === '__none__' ? '-' : this.tituloLabel(key),
          sharesCountForTitulo: entry.sharesCount,
          sharesCountAdjustedForTitulo: entry.adjusted,
          isFirst: i === 0,
          isUsufructo,
        });
      });
    });

    return result;
  });

  /** Normaliza id de serie (API puede devolver number o string). */
  private normalizeSeriesKey(id: number | string | null | undefined): number | null {
    if (id == null || id === '') return null;
    const n = Number(id);
    return Number.isNaN(n) ? null : n;
  }

  private isUsufructoTitulo(titulo: string | undefined | null): boolean {
    return (titulo ?? '').trim().toLowerCase() === 'usufructo';
  }

  /**
   * Acciones ajustadas del socio: Σ (sharesCount × % titularidad).
   * Excluye usufructo salvo que se pida con includeUsufructo.
   */
  private partnerTotalAdjustedShares(
    numberings: any[] | undefined | null,
    opts?: { includeUsufructo?: boolean }
  ): number {
    if (!numberings?.length) return 0;
    const includeUsufructo = opts?.includeUsufructo === true;
    const list = includeUsufructo
      ? numberings
      : numberings.filter(n => !this.isUsufructoTitulo(n.titulo));
    return list.reduce((acc, n) => {
      const pct = Number(n.ownershipPercentage ?? 0) / 100;
      return acc + (n.sharesCount ?? 0) * pct;
    }, 0);
  }

  /** Suma acciones de las numeraciones mostradas en el modal (Número total). */
  readonly modalNumeracionesTotalShares = computed(() =>
    this.store.shareNumberings().reduce((sum, n) => sum + (n.sharesCount || 0), 0)
  );

  /**
   * Σ subtotales ajustados (sin usufructo), todas las series — como el mockup "TOTAL ACCIONES AJUSTADAS".
   */
  readonly modalTotalAccionesAjustadas = computed(() =>
    this.groupedShareNumberings().reduce((s, g) => s + g.subtotalAdjusted, 0)
  );

  /** VN total del socio (€): Σ (acciones ajustadas × VN serie), excl. usufructo — para % participación en listado. */
  private partnerTotalVnEuro(shareNumberings: any[] | undefined | null): number {
    if (!shareNumberings?.length) return 0;
    const hasSeries = this.capital.hasSeries();
    let sum = 0;
    for (const n of shareNumberings) {
      if (this.isUsufructoTitulo(n.titulo)) continue;
      const sid = hasSeries
        ? this.normalizeSeriesKey(n.seriesId ?? n.socialCapital?.id ?? n.series?.id)
        : null;
      const meta = this.resolveSeriesMeta(
        hasSeries ? sid : null,
        n.seriesName || n.socialCapital?.name || n.series?.name,
      );
      const sharesCount = n.sharesCount ?? n.sharesNum ?? 0;
      const pct = Number(n.ownershipPercentage ?? 0) / 100;
      const nominal = meta.nominalValue || Number(n.nominalValue) || Number(n.socialCapital?.nominalValue) || 0;
      sum += sharesCount * pct * nominal;
    }
    return sum;
  }

  /** Numeraciones agrupadas por seriesId (igual que readonly) + series de capital vacías. */
  readonly groupedShareNumberings = computed((): GroupedShareSeries[] =>
    buildGroupedShareSeriesForModal(this.store.shareNumberings(), {
      hasSeries: this.capital.hasSeries(),
      capitalSeries: this.capital.series().map(s => ({
        id: s.id,
        name: s.name,
        totalShares: s.totalShares,
        nominalValue: s.nominalValue,
      })),
      singleSeriesTotal: this.capital.singleSeriesTotal(),
      singleSeriesNominal: Number(this.capital.capitalSocialForm.get('nominalValue')?.value) || 0,
    }) as GroupedShareSeries[],
  );

  /**
   * TOTAL SERIE X: acciones ajustadas del socio en esa serie / acciones totales de esa serie en la empresa.
   * (Definición del mockup EJEMPLO.)
   */
  readonly seriesTotals = computed((): SeriesParticipationTotal[] => {
    const groups = this.groupedShareNumberings().filter(g => g.hasNonUsufructo);
    return groups.map(g => {
      const denom = g.seriesTotalShares;
      return {
        seriesName: g.seriesName,
        adjusted: g.subtotalAdjusted,
        denominator: denom,
        fractionLabel:
          denom > 0
            ? `${this.formatNumberEs(g.subtotalAdjusted)} / ${this.formatNumberEs(denom)}`
            : '-',
        percentLabel:
          denom > 0 ? formatPct4((g.subtotalAdjusted / denom) * 100) : '-'
      };
    });
  });

  private resolveSeriesMeta(
    seriesId: number | null,
    seriesName?: string,
  ): { name: string; totalShares: number; nominalValue: number } {
    const hasSeries = this.capital.hasSeries();
    if (!hasSeries) {
      const nv = Number(this.capital.capitalSocialForm.get('nominalValue')?.value) || 0;
      return {
        name: 'Serie única',
        totalShares: this.capital.singleSeriesTotal(),
        nominalValue: nv
      };
    }
    if (seriesId === null) {
      if (seriesName) {
        const byName = this.capital.series().find(
          x => x.name.trim().toLowerCase() === seriesName.trim().toLowerCase(),
        );
        if (byName) {
          return {
            name: byName.name,
            totalShares: byName.totalShares ?? 0,
            nominalValue: Number(byName.nominalValue) || 0,
          };
        }
      }
      return { name: 'Sin serie', totalShares: 0, nominalValue: 0 };
    }
    const sid = this.normalizeSeriesKey(seriesId);
    const s = this.capital.series().find(
      x =>
        this.normalizeSeriesKey(x.id) === sid ||
        (x.feLocalId != null && this.normalizeSeriesKey(x.feLocalId) === sid),
    );
    if (s) {
      return {
        name: s.name,
        totalShares: s.totalShares ?? 0,
        nominalValue: Number(s.nominalValue) || 0,
      };
    }
    if (seriesName) {
      const byName = this.capital.series().find(
        x => x.name.trim().toLowerCase() === seriesName.trim().toLowerCase(),
      );
      if (byName) {
        return {
          name: byName.name,
          totalShares: byName.totalShares ?? 0,
          nominalValue: Number(byName.nominalValue) || 0,
        };
      }
    }
    return {
      name: seriesName || 'Serie',
      totalShares: 0,
      nominalValue: 0,
    };
  }

  private formatNumberEs(value: number): string {
    return formatShareAmountEs(value, 4);
  }

  formatAdjusted(value: number): string {
    return formatShareAmountEs(value, 4);
  }

  /** Total acciones ajustadas (modal / resumen). */
  formatModalTotalAccionesAjustadas(value: number): string {
    return formatShareAmountEs(value, 4);
  }

  removeSeriesGroup(group: GroupedShareSeries, event?: Event): void {
    event?.stopPropagation();
    event?.preventDefault();
    if (!this.capital.hasSeries()) {
      this.store.removeNumeracionesAtIndices(group.rows.map(r => r.globalIndex));
      return;
    }
    this.store.removeNumeracionBySeries(group.seriesId);
  }

  removeNumeracionRow(globalIndex: number, event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    this.store.removeNumeracion(globalIndex);
  }

  tituloLabel(titulo: string | undefined): string {
    return formatTituloLabel(titulo);
  }

  readonly formatPct4 = formatPct4;

  onSocioRowEnter(socioIndex: number): void {
    this.hoveredSocioIndex.set(socioIndex);
  }

  onSociosTableBodyLeave(): void {
    this.hoveredSocioIndex.set(null);
  }

  /** Delegación: rowspan y subfila subtotal bajo el mismo `tr` detectable. */
  onGroupedSharesTableMouseOver(event: MouseEvent): void {
    const tr = (event.target as HTMLElement | null)?.closest?.(
      'tr[data-share-group-id]'
    ) as HTMLTableRowElement | null;
    const id = tr?.dataset['shareGroupId'];
    if (id != null && id !== '' && this.hoveredShareGroupId() !== id) {
      this.hoveredShareGroupId.set(id);
    }
  }

  onGroupedSharesTableBodyLeave(): void {
    this.hoveredShareGroupId.set(null);
  }

  saveSocio(): void {
    this.store.saveSocio(this.clientId(), this.deferPersistence());
  }

  saveNumeracion(): void {
    this.store.saveNumeracionModal(
      this.capital.seriesOptions(),
      this.capital.seriesTotalsMap(),
      this.capital.singleSeriesTotal()
    );
  }

  openNumeracionModalForGroup(group: GroupedShareSeries): void {
    this.store.openNumeracionModalForGroup(
      group.seriesId,
      group.rows.map(r => r.globalIndex),
    );
  }

  /** Cast necesario para usar [formGroupName] con un AbstractControl del FormArray. */
  asFormGroup(ctrl: AbstractControl): FormGroup {
    return ctrl as FormGroup;
  }

  goToSocioDetail(index: number): void {
    const socio = this.store.socios()[index];
    const personId = socio?.associatedClient?.id;
    if (personId) {
      navigateToRelatedClient(this.router, personId, this.activeTab() || 'constitution');
    }
  }
}
